import type { MonPaiementAbonnement } from "../types";
import { methodeExigePreuve, methodeIcon, methodeLabel, periodiciteLabel } from "../data";
import { formatDate, formatFcfa } from "@/utils/format";
import { cn } from "@/utils/cn";
import PaiementStatutBadge from "./PaiementStatutBadge";

interface PaiementTableProps {
  paiements: MonPaiementAbonnement[];
  onViewPreuve: (paiement: MonPaiementAbonnement) => void;
}

/** Tableau desktop de l'historique des paiements d'abonnement. */
export default function PaiementTable({ paiements, onViewPreuve }: PaiementTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="w-full min-w-[900px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 bg-background-100/70">
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Date</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Formule</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Période</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Montant</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Moyen</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Référence</th>
            <th scope="col" className="px-4 py-3 text-xs font-label font-semibold text-foreground-700">Statut</th>
            <th scope="col" className="px-4 py-3 text-right text-xs font-label font-semibold text-foreground-700">Preuve</th>
          </tr>
        </thead>
        <tbody>
          {paiements.map((paiement) => {
            const refuse = paiement.statut === "rejete";
            return (
              <tr key={paiement.id} className="border-b border-background-200/70 last:border-b-0 align-top">
                <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                  {formatDate(paiement.dateSoumission)}
                </td>
                <td className="px-4 py-3.5 text-sm font-label font-medium text-foreground-950">
                  {paiement.formuleNom || "—"}
                </td>
                <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                  {periodiciteLabel(paiement.periodicite)}
                </td>
                <td className="whitespace-nowrap px-4 py-3.5 text-sm font-label font-medium text-foreground-950">
                  {formatFcfa(paiement.montant)}
                </td>
                <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                  <span className="inline-flex items-center gap-1.5">
                    <i className={cn(methodeIcon(paiement.mode), "text-base text-foreground-400")} aria-hidden="true" />
                    {methodeLabel(paiement.mode)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3.5 text-sm text-foreground-700">
                  {paiement.reference || "—"}
                </td>
                <td className="px-4 py-3.5">
                  <PaiementStatutBadge statut={paiement.statut} />
                  {paiement.statut === "valide" && paiement.dateValidation && (
                    <p className="mt-1.5 text-xs text-foreground-500">
                      Validé le {formatDate(paiement.dateValidation)}
                    </p>
                  )}
                  {paiement.statut === "en_attente" && (
                    <p className="mt-1.5 max-w-[15rem] text-xs text-foreground-500">
                      En cours de traitement par notre équipe.
                    </p>
                  )}
                  {refuse && (
                    <p className="mt-1.5 max-w-[15rem] text-xs text-primary-800">
                      Motif : {paiement.motif || "Aucun motif renseigné"}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3.5 text-right">
                  {methodeExigePreuve(paiement.mode) && paiement.preuveUrl ? (
                    <button
                      type="button"
                      onClick={() => onViewPreuve(paiement)}
                      title="Voir la preuve de paiement"
                      aria-label="Voir la preuve de paiement"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-accent-300 bg-accent-50 text-accent-800 transition-colors hover:bg-accent-100 cursor-pointer"
                    >
                      <i className="ri-file-image-line text-base" aria-hidden="true" />
                    </button>
                  ) : (
                    <span className="text-sm text-foreground-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}